import type { Locale } from "../localization/constants";
import type { EventManagerTranslations } from "./eventManagerTranslations";
import {
  eventManagerTranslationsEn,
  eventManagerTranslationsEs,
  eventManagerTranslationsFr,
  eventManagerTranslationsPt,
} from "./eventManagerTranslations";

export type AdminUiTranslations = {
  common: {
    email: string;
    unknownError: string;
    selectActiveEvent: string;
  };
  addParticipants: {
    button: string;
    ariaLabel: string;
    title: string;
    description: string;
    countLabel: string;
    confirm: string;
    invalidCount: string;
    success: string;
    error: string;
  };
  addTeams: {
    button: string;
    ariaLabel: string;
    title: string;
    description: string;
    countLabel: string;
    confirm: string;
    invalidCount: string;
    success: string;
    error: string;
  };
  bulkInvite: {
    button: string;
    ariaLabel: string;
    title: string;
    description: string;
    invalidRows: string;
    summary: string;
    rowError: string;
    snackbarSummary: string;
    importFailed: string;
  };
  changeTeam: {
    entityAction: string;
    teamUpdated: string;
    error: string;
  };
  approveStandby: {
    entityAction: string;
    ariaLabel: string;
    title: string;
    body: string;
    cancel: string;
    autoMatch: string;
    chooseTeam: string;
    success: string;
    error: string;
    priorJoinBody: string;
  };
  removeUserFromEvent: {
    entityAction: string;
    title: string;
    body: string;
    success: string;
    error: string;
  };
  removeUserFromAccount: {
    entityAction: string;
    title: string;
    body: string;
    success: string;
    error: string;
    selfNotAllowed: string;
  };
  sendEventInvitationEmail: {
    entityAction: string;
    sending: string;
    success: string;
    cooldown: string;
    disabled: string;
    error: string;
  };
  cloneSpace: {
    entityAction: string;
    title: string;
    body: string;
    success: string;
    error: string;
  };
  learningPathImport: {
    button: string;
    entityAction: string;
    ariaLabel: string;
    titleCreate: string;
    titleUpdate: string;
    description: string;
    parseFailed: string;
    graphFailed: string;
    summary: string;
    rowError: string;
    snackbarCreated: string;
    snackbarUpdated: string;
    importFailed: string;
  };
  contentImport: {
    button: string;
    ariaLabel: string;
    title: string;
    description: string;
    parseFailed: string;
    summary: string;
    rowError: string;
    snackbarSummary: string;
    importFailed: string;
  };
  notificationDefsImport: {
    button: string;
    ariaLabel: string;
    title: string;
    description: string;
    parseFailed: string;
    summary: string;
    rowError: string;
    snackbarSummary: string;
    importFailed: string;
  };
  eventUserProfile: {
    column: string;
    viewProfile: string;
    dialogTitle: string;
    dialogAria: string;
    close: string;
    noName: string;
    emailVerified: string;
    emailUnverified: string;
    accountActive: string;
    accountDisabled: string;
    sectionContact: string;
    sectionSignIn: string;
    sectionActivity: string;
    email: string;
    phone: string;
    noPhone: string;
    noProviders: string;
    providerGoogle: string;
    providerPassword: string;
    providerApple: string;
    providerPhone: string;
    providerOther: string;
    createdAt: string;
    lastSignIn: string;
    lastActive: string;
    never: string;
    userId: string;
    copyId: string;
    copied: string;
  };
  buildVersion: {
    current: string;
    updateAvailable: string;
    tooltipCurrent: string;
    tooltipUpdate: string;
  };
} & EventManagerTranslations;

export const adminUiTranslations: Record<Locale, AdminUiTranslations> = {
  en: {
    common: {
      email: "Email",
      unknownError: "Unknown error",
      selectActiveEvent: "Select an active event",
    },
    addParticipants: {
      button: "Add slots",
      ariaLabel: "Add participant slots",
      title: "Add participant slots",
      description: "How many slots do you want to add to this session?",
      countLabel: "Number of slots",
      confirm: "Add",
      invalidCount: "Enter a whole number greater than zero",
      success: "{{added}} slots added ({{participantSlotCount}} total)",
      error: "Could not add slots: {{message}}",
    },
    addTeams: {
      button: "Add teams",
      ariaLabel: "Add teams",
      title: "Add teams",
      description: "How many teams do you want to add to this session?",
      countLabel: "Number of teams",
      confirm: "Add",
      invalidCount: "Enter a whole number greater than zero",
      success: "{{added}} teams added ({{teamSlotCount}} total)",
      error: "Could not add teams: {{message}}",
    },
    bulkInvite: {
      button: "Import invitations",
      ariaLabel: "Import event invitations from CSV",
      title: "Import invitations from CSV",
      description:
        "Export the list from Google Docs as CSV. Columns: email, role (optional; defaults to participant).",
      invalidRows: "No valid rows found. Format: email,role",
      summary: "{{invited}} invited, {{failed}} failed",
      rowError: "{{email}} ({{role}}): {{reason}}",
      snackbarSummary: "{{invited}} invited, {{failed}} failed",
      importFailed: "Import failed: {{message}}",
    },
    changeTeam: {
      entityAction: "Change team",
      teamUpdated: "Team updated",
      error: "Could not change team: {{message}}",
    },
    approveStandby: {
      entityAction: "Approve",
      ariaLabel: "Approve user into session",
      title: "Approve user",
      body: "Assign a slot and team so this user can enter the session. If team assignment fails, they stay awaiting approval.",
      cancel: "Cancel",
      autoMatch: "Auto-match team",
      chooseTeam: "Choose team…",
      success: "User approved",
      error: "Could not approve user: {{message}}",
      priorJoinBody:
        "This email already joined this session. If you approve them, they start over, and the approval is recorded under your name.",
    },
    removeUserFromEvent: {
      entityAction: "Remove from event",
      title: "Remove member from event",
      body: "This removes the user's membership from this event. They can join again later if invited or via access code.",
      success: "Member removed from event",
      error: "Could not remove member: {{message}}",
    },
    removeUserFromAccount: {
      entityAction: "Revoke access",
      title: "Revoke account access",
      body: "This removes the user's account manager membership. They can be invited again later.",
      success: "Account access revoked",
      error: "Could not revoke access: {{message}}",
      selfNotAllowed: "You cannot revoke your own access",
    },
    sendEventInvitationEmail: {
      entityAction: "Send email",
      sending: "Sending the invitation email to {{email}}…",
      success: "Invitation email sent",
      cooldown: "An email was already sent. Wait an hour before sending another.",
      disabled: "Outbound email is turned off.",
      error: "Could not send the email: {{message}}",
    },
    cloneSpace: {
      entityAction: "Clone learning path",
      title: "Clone learning path",
      body: "Creates a full copy of this learning path and its content. This may take a minute.",
      success: "Learning path cloned",
      error: "Could not clone learning path: {{message}}",
    },
    learningPathImport: {
      button: "Import JSON",
      entityAction: "Import JSON",
      ariaLabel: "Import a learning path from a JSON file",
      titleCreate: "Import learning path from JSON",
      titleUpdate: "Import into this learning path",
      description:
        "Upload a learning-path.json bundle with the path fields plus units, stories, questions, and levels. See docs/content-import.",
      parseFailed: "Invalid file: {{message}}",
      graphFailed:
        "The file has {{count}} validation error(s). Nothing was imported.",
      summary: "{{imported}} items imported, {{failed}} failed",
      rowError: "{{key}}: {{reason}}",
      snackbarCreated: "Learning path created ({{imported}} items, {{failed}} failed)",
      snackbarUpdated: "Learning path updated ({{imported}} items, {{failed}} failed)",
      importFailed: "Import failed: {{message}}",
    },
    contentImport: {
      button: "Import JSON",
      ariaLabel: "Import content from JSON file",
      title: "Import {{collection}} from JSON",
      description:
        "Upload a .json file. See docs/content-import in the repository for format and sample fixtures.",
      parseFailed: "Invalid file: {{message}}",
      summary: "{{imported}} imported, {{failed}} failed",
      rowError: "{{key}}: {{reason}}",
      snackbarSummary: "{{imported}} imported, {{failed}} failed",
      importFailed: "Import failed: {{message}}",
    },
    notificationDefsImport: {
      button: "Import JSON",
      ariaLabel: "Import notification templates from JSON file",
      title: "Import notification templates from JSON",
      description:
        "Upload a .json file. Replaces or creates each template by type. See docs/content-import (fixture + schema).",
      parseFailed: "Invalid file: {{message}}",
      summary: "{{imported}} imported, {{failed}} failed",
      rowError: "{{key}}: {{reason}}",
      snackbarSummary: "{{imported}} imported, {{failed}} failed",
      importFailed: "Import failed: {{message}}",
    },
    eventUserProfile: {
      column: "Profile",
      viewProfile: "View profile",
      dialogTitle: "Member profile",
      dialogAria: "Member sign-in profile",
      close: "Close",
      noName: "No display name",
      emailVerified: "Verified email",
      emailUnverified: "Email not verified",
      accountActive: "Active",
      accountDisabled: "Disabled",
      sectionContact: "Contact",
      sectionSignIn: "How they sign in",
      sectionActivity: "Activity",
      email: "Email",
      phone: "Phone",
      noPhone: "No phone number",
      noProviders: "No sign-in methods linked",
      providerGoogle: "Google",
      providerPassword: "Email and password",
      providerApple: "Apple",
      providerPhone: "Phone",
      providerOther: "{{provider}}",
      createdAt: "Account created",
      lastSignIn: "Last sign-in",
      lastActive: "Last active",
      never: "Never",
      userId: "User ID",
      copyId: "Copy ID",
      copied: "Copied",
    },
    buildVersion: {
      current: "{{sha}}",
      updateAvailable: "Update · {{sha}}",
      tooltipCurrent: "Build {{sha}} · {{builtAt}}",
      tooltipUpdate: "Newer deploy available. Click to reload. Built {{builtAt}}",
    },
    ...eventManagerTranslationsEn,
  },
  es: {
    common: {
      email: "Email",
      unknownError: "Error desconocido",
      selectActiveEvent: "Seleccioná un evento activo",
    },
    addParticipants: {
      button: "Agregar slots",
      ariaLabel: "Agregar slots de participantes",
      title: "Agregar slots de participantes",
      description: "¿Cuántos slots querés agregar a esta sesión?",
      countLabel: "Cantidad de slots",
      confirm: "Agregar",
      invalidCount: "Ingresá un número entero mayor a cero",
      success: "{{added}} slots agregados ({{participantSlotCount}} en total)",
      error: "No se pudieron agregar slots: {{message}}",
    },
    addTeams: {
      button: "Agregar equipos",
      ariaLabel: "Agregar equipos",
      title: "Agregar equipos",
      description: "¿Cuántos equipos querés agregar a esta sesión?",
      countLabel: "Cantidad de equipos",
      confirm: "Agregar",
      invalidCount: "Ingresá un número entero mayor a cero",
      success: "{{added}} equipos agregados ({{teamSlotCount}} en total)",
      error: "No se pudieron agregar equipos: {{message}}",
    },
    bulkInvite: {
      button: "Importar invitaciones",
      ariaLabel: "Importar invitaciones al evento desde CSV",
      title: "Importar invitaciones desde CSV",
      description:
        "Exportá el listado desde Google Docs como CSV. Columnas: email, rol (opcional; por defecto participant).",
      invalidRows: "No se encontraron filas válidas. Formato: email,rol",
      summary: "{{invited}} invitados, {{failed}} fallidos",
      rowError: "{{email}} ({{role}}): {{reason}}",
      snackbarSummary: "{{invited}} invitados, {{failed}} fallidos",
      importFailed: "Importación fallida: {{message}}",
    },
    changeTeam: {
      entityAction: "Cambiar de equipo",
      teamUpdated: "Equipo actualizado",
      error: "No se pudo cambiar de equipo: {{message}}",
    },
    approveStandby: {
      entityAction: "Aprobar",
      ariaLabel: "Aprobar usuario en la sesión",
      title: "Aprobar usuario",
      body: "Asigná cupo y equipo para que entre a la sesión. Si falla la asignación de equipo, sigue esperando aprobación.",
      cancel: "Cancelar",
      autoMatch: "Auto-asignar equipo",
      chooseTeam: "Elegir equipo…",
      success: "Usuario aprobado",
      error: "No se pudo aprobar al usuario: {{message}}",
      priorJoinBody:
        "Este correo ya ingresó a esta sesión. Si lo aprobás, entra de cero y la aprobación queda a tu nombre.",
    },
    removeUserFromEvent: {
      entityAction: "Quitar del evento",
      title: "Quitar miembro del evento",
      body: "Se elimina la membresía de este usuario en el evento. Puede volver a unirse más tarde si lo invitan o con el código de acceso.",
      success: "Miembro quitado del evento",
      error: "No se pudo quitar al miembro: {{message}}",
    },
    removeUserFromAccount: {
      entityAction: "Revocar acceso",
      title: "Revocar acceso a la cuenta",
      body: "Se elimina la membresía de manager de este usuario. Se lo puede invitar de nuevo más tarde.",
      success: "Acceso a la cuenta revocado",
      error: "No se pudo revocar el acceso: {{message}}",
      selfNotAllowed: "No podés revocar tu propio acceso",
    },
    sendEventInvitationEmail: {
      entityAction: "Enviar email",
      sending: "Enviando el email de invitación a {{email}}…",
      success: "Email de invitación enviado",
      cooldown: "Ya se envió un email. Esperá una hora para mandar otro.",
      disabled: "El envío de email está desactivado.",
      error: "No se pudo enviar el email: {{message}}",
    },
    cloneSpace: {
      entityAction: "Clonar learning path",
      title: "Clonar learning path",
      body: "Crea una copia completa de este learning path y su contenido. Puede demorar un minuto.",
      success: "Learning path clonado",
      error: "No se pudo clonar el learning path: {{message}}",
    },
    learningPathImport: {
      button: "Importar JSON",
      entityAction: "Importar JSON",
      ariaLabel: "Importar un learning path desde un archivo JSON",
      titleCreate: "Importar learning path desde JSON",
      titleUpdate: "Importar en este learning path",
      description:
        "Subí un learning-path.json con los campos del path más units, stories, questions y levels. Ver docs/content-import.",
      parseFailed: "Archivo inválido: {{message}}",
      graphFailed:
        "El archivo tiene {{count}} error(es) de validación. No se importó nada.",
      summary: "{{imported}} ítems importados, {{failed}} fallidos",
      rowError: "{{key}}: {{reason}}",
      snackbarCreated: "Learning path creado ({{imported}} ítems, {{failed}} fallidos)",
      snackbarUpdated: "Learning path actualizado ({{imported}} ítems, {{failed}} fallidos)",
      importFailed: "Importación fallida: {{message}}",
    },
    contentImport: {
      button: "Importar JSON",
      ariaLabel: "Importar contenido desde archivo JSON",
      title: "Importar {{collection}} desde JSON",
      description:
        "Subí un archivo .json. Ver docs/content-import en el repositorio para el formato y fixtures de ejemplo.",
      parseFailed: "Archivo inválido: {{message}}",
      summary: "{{imported}} importados, {{failed}} fallidos",
      rowError: "{{key}}: {{reason}}",
      snackbarSummary: "{{imported}} importados, {{failed}} fallidos",
      importFailed: "Importación fallida: {{message}}",
    },
    notificationDefsImport: {
      button: "Importar JSON",
      ariaLabel: "Importar plantillas de notificación desde JSON",
      title: "Importar plantillas de notificación",
      description:
        "Subí un archivo .json. Reemplaza o crea cada plantilla por type. Ver docs/content-import (fixture + schema).",
      parseFailed: "Archivo inválido: {{message}}",
      summary: "{{imported}} importados, {{failed}} fallidos",
      rowError: "{{key}}: {{reason}}",
      snackbarSummary: "{{imported}} importados, {{failed}} fallidos",
      importFailed: "Importación fallida: {{message}}",
    },
    eventUserProfile: {
      column: "Perfil",
      viewProfile: "Ver perfil",
      dialogTitle: "Perfil del miembro",
      dialogAria: "Perfil de inicio de sesión del miembro",
      close: "Cerrar",
      noName: "Sin nombre",
      emailVerified: "Email verificado",
      emailUnverified: "Email sin verificar",
      accountActive: "Activa",
      accountDisabled: "Deshabilitada",
      sectionContact: "Contacto",
      sectionSignIn: "Cómo inicia sesión",
      sectionActivity: "Actividad",
      email: "Email",
      phone: "Teléfono",
      noPhone: "Sin teléfono",
      noProviders: "Sin métodos de inicio de sesión",
      providerGoogle: "Google",
      providerPassword: "Email y contraseña",
      providerApple: "Apple",
      providerPhone: "Teléfono",
      providerOther: "{{provider}}",
      createdAt: "Cuenta creada",
      lastSignIn: "Último inicio de sesión",
      lastActive: "Última actividad",
      never: "Nunca",
      userId: "ID de usuario",
      copyId: "Copiar ID",
      copied: "Copiado",
    },
    buildVersion: {
      current: "{{sha}}",
      updateAvailable: "Actualizar · {{sha}}",
      tooltipCurrent: "Build {{sha}} · {{builtAt}}",
      tooltipUpdate: "Hay un deploy más nuevo. Clic para recargar. Build {{builtAt}}",
    },
    ...eventManagerTranslationsEs,
  },
  pt: {
    common: {
      email: "Email",
      unknownError: "Erro desconhecido",
      selectActiveEvent: "Selecione um evento ativo",
    },
    addParticipants: {
      button: "Adicionar slots",
      ariaLabel: "Adicionar slots de participantes",
      title: "Adicionar slots de participantes",
      description: "Quantos slots você quer adicionar a esta sessão?",
      countLabel: "Quantidade de slots",
      confirm: "Adicionar",
      invalidCount: "Informe um número inteiro maior que zero",
      success: "{{added}} slots adicionados ({{participantSlotCount}} no total)",
      error: "Não foi possível adicionar slots: {{message}}",
    },
    addTeams: {
      button: "Adicionar equipes",
      ariaLabel: "Adicionar equipes",
      title: "Adicionar equipes",
      description: "Quantas equipes você quer adicionar a esta sessão?",
      countLabel: "Quantidade de equipes",
      confirm: "Adicionar",
      invalidCount: "Informe um número inteiro maior que zero",
      success: "{{added}} equipes adicionadas ({{teamSlotCount}} no total)",
      error: "Não foi possível adicionar equipes: {{message}}",
    },
    bulkInvite: {
      button: "Importar convites",
      ariaLabel: "Importar convites do evento de CSV",
      title: "Importar convites de CSV",
      description:
        "Exporte a lista do Google Docs como CSV. Colunas: email, papel (opcional; padrão participant).",
      invalidRows: "Nenhuma linha válida encontrada. Formato: email,papel",
      summary: "{{invited}} convidados, {{failed}} com falha",
      rowError: "{{email}} ({{role}}): {{reason}}",
      snackbarSummary: "{{invited}} convidados, {{failed}} com falha",
      importFailed: "Falha na importação: {{message}}",
    },
    changeTeam: {
      entityAction: "Mudar de equipe",
      teamUpdated: "Equipe atualizada",
      error: "Não foi possível mudar de equipe: {{message}}",
    },
    approveStandby: {
      entityAction: "Aprovar",
      ariaLabel: "Aprovar usuário na sessão",
      title: "Aprovar usuário",
      body: "Atribua vaga e equipe para entrar na sessão. Se a atribuição de equipe falhar, o usuário continua aguardando aprovação.",
      cancel: "Cancelar",
      autoMatch: "Autoatribuir equipe",
      chooseTeam: "Escolher equipe…",
      success: "Usuário aprovado",
      error: "Não foi possível aprovar o usuário: {{message}}",
      priorJoinBody:
        "Este e-mail já entrou nesta sessão. Se você aprovar, a pessoa começa do zero e a aprovação fica no seu nome.",
    },
    removeUserFromEvent: {
      entityAction: "Remover do evento",
      title: "Remover membro do evento",
      body: "Isso remove a associação do usuário neste evento. Ele pode entrar de novo depois se for convidado ou com o código de acesso.",
      success: "Membro removido do evento",
      error: "Não foi possível remover o membro: {{message}}",
    },
    removeUserFromAccount: {
      entityAction: "Revogar acesso",
      title: "Revogar acesso à conta",
      body: "Isso remove a associação de manager deste usuário. Ele pode ser convidado de novo depois.",
      success: "Acesso à conta revogado",
      error: "Não foi possível revogar o acesso: {{message}}",
      selfNotAllowed: "Você não pode revogar o próprio acesso",
    },
    sendEventInvitationEmail: {
      entityAction: "Enviar email",
      sending: "Enviando o email de convite para {{email}}…",
      success: "Email de convite enviado",
      cooldown: "Já foi enviado um email. Espere uma hora para enviar outro.",
      disabled: "O envio de email está desativado.",
      error: "Não foi possível enviar o email: {{message}}",
    },
    cloneSpace: {
      entityAction: "Clonar learning path",
      title: "Clonar learning path",
      body: "Cria uma cópia completa deste learning path e do seu conteúdo. Isso pode levar um minuto.",
      success: "Learning path clonado",
      error: "Não foi possível clonar o learning path: {{message}}",
    },
    learningPathImport: {
      button: "Importar JSON",
      entityAction: "Importar JSON",
      ariaLabel: "Importar um learning path de um arquivo JSON",
      titleCreate: "Importar learning path de JSON",
      titleUpdate: "Importar neste learning path",
      description:
        "Envie um learning-path.json com os campos do path mais units, stories, questions e levels. Veja docs/content-import.",
      parseFailed: "Arquivo inválido: {{message}}",
      graphFailed:
        "O arquivo tem {{count}} erro(s) de validação. Nada foi importado.",
      summary: "{{imported}} itens importados, {{failed}} com falha",
      rowError: "{{key}}: {{reason}}",
      snackbarCreated: "Learning path criado ({{imported}} itens, {{failed}} com falha)",
      snackbarUpdated: "Learning path atualizado ({{imported}} itens, {{failed}} com falha)",
      importFailed: "Falha na importação: {{message}}",
    },
    contentImport: {
      button: "Importar JSON",
      ariaLabel: "Importar conteúdo de arquivo JSON",
      title: "Importar {{collection}} de JSON",
      description:
        "Envie um arquivo .json. Veja docs/content-import no repositório para formato e fixtures de exemplo.",
      parseFailed: "Arquivo inválido: {{message}}",
      summary: "{{imported}} importados, {{failed}} com falha",
      rowError: "{{key}}: {{reason}}",
      snackbarSummary: "{{imported}} importados, {{failed}} com falha",
      importFailed: "Falha na importação: {{message}}",
    },
    notificationDefsImport: {
      button: "Importar JSON",
      ariaLabel: "Importar modelos de notificação do JSON",
      title: "Importar modelos de notificação",
      description:
        "Envie um arquivo .json. Substitui ou cria cada modelo por type. Veja docs/content-import (fixture + schema).",
      parseFailed: "Arquivo inválido: {{message}}",
      summary: "{{imported}} importados, {{failed}} com falha",
      rowError: "{{key}}: {{reason}}",
      snackbarSummary: "{{imported}} importados, {{failed}} com falha",
      importFailed: "Falha na importação: {{message}}",
    },
    eventUserProfile: {
      column: "Perfil",
      viewProfile: "Ver perfil",
      dialogTitle: "Perfil do membro",
      dialogAria: "Perfil de login do membro",
      close: "Fechar",
      noName: "Sem nome",
      emailVerified: "Email verificado",
      emailUnverified: "Email não verificado",
      accountActive: "Ativa",
      accountDisabled: "Desativada",
      sectionContact: "Contato",
      sectionSignIn: "Como entra",
      sectionActivity: "Atividade",
      email: "Email",
      phone: "Telefone",
      noPhone: "Sem telefone",
      noProviders: "Nenhum método de login",
      providerGoogle: "Google",
      providerPassword: "Email e senha",
      providerApple: "Apple",
      providerPhone: "Telefone",
      providerOther: "{{provider}}",
      createdAt: "Conta criada",
      lastSignIn: "Último login",
      lastActive: "Última atividade",
      never: "Nunca",
      userId: "ID do usuário",
      copyId: "Copiar ID",
      copied: "Copiado",
    },
    buildVersion: {
      current: "{{sha}}",
      updateAvailable: "Atualizar · {{sha}}",
      tooltipCurrent: "Build {{sha}} · {{builtAt}}",
      tooltipUpdate: "Há um deploy mais novo. Clique para recarregar. Build {{builtAt}}",
    },
    ...eventManagerTranslationsPt,
  },
  fr: {
    common: {
      email: "Email",
      unknownError: "Erreur inconnue",
      selectActiveEvent: "Sélectionnez un événement actif",
    },
    addParticipants: {
      button: "Ajouter des slots",
      ariaLabel: "Ajouter des slots participants",
      title: "Ajouter des slots participants",
      description: "Combien de slots voulez-vous ajouter à cette session ?",
      countLabel: "Nombre de slots",
      confirm: "Ajouter",
      invalidCount: "Saisissez un nombre entier supérieur à zéro",
      success: "{{added}} slots ajoutés ({{participantSlotCount}} au total)",
      error: "Impossible d'ajouter des slots : {{message}}",
    },
    addTeams: {
      button: "Ajouter des équipes",
      ariaLabel: "Ajouter des équipes",
      title: "Ajouter des équipes",
      description: "Combien d'équipes voulez-vous ajouter à cette session ?",
      countLabel: "Nombre d'équipes",
      confirm: "Ajouter",
      invalidCount: "Saisissez un nombre entier supérieur à zéro",
      success: "{{added}} équipes ajoutées ({{teamSlotCount}} au total)",
      error: "Impossible d'ajouter des équipes : {{message}}",
    },
    bulkInvite: {
      button: "Importer des invitations",
      ariaLabel: "Importer des invitations à l'événement depuis CSV",
      title: "Importer des invitations depuis CSV",
      description:
        "Exportez la liste depuis Google Docs en CSV. Colonnes : email, rôle (optionnel ; participant par défaut).",
      invalidRows: "Aucune ligne valide. Format : email,rôle",
      summary: "{{invited}} invités, {{failed}} en échec",
      rowError: "{{email}} ({{role}}) : {{reason}}",
      snackbarSummary: "{{invited}} invités, {{failed}} en échec",
      importFailed: "Échec de l'import : {{message}}",
    },
    changeTeam: {
      entityAction: "Changer d'équipe",
      teamUpdated: "Équipe mise à jour",
      error: "Impossible de changer d'équipe : {{message}}",
    },
    approveStandby: {
      entityAction: "Approuver",
      ariaLabel: "Approuver l'utilisateur dans la session",
      title: "Approuver l'utilisateur",
      body: "Attribuez une place et une équipe pour entrer dans la session. Si l'attribution d'équipe échoue, l'utilisateur reste en attente d'approbation.",
      cancel: "Annuler",
      autoMatch: "Attribution auto d'équipe",
      chooseTeam: "Choisir une équipe…",
      success: "Utilisateur approuvé",
      error: "Impossible d'approuver l'utilisateur : {{message}}",
      priorJoinBody:
        "Cet e-mail a déjà rejoint cette session. Si vous l'approuvez, il recommence à zéro et l'approbation est enregistrée à votre nom.",
    },
    removeUserFromEvent: {
      entityAction: "Retirer de l'événement",
      title: "Retirer le membre de l'événement",
      body: "Cela retire l'appartenance de l'utilisateur à cet événement. Il pourra rejoindre plus tard s'il est invité ou via le code d'accès.",
      success: "Membre retiré de l'événement",
      error: "Impossible de retirer le membre : {{message}}",
    },
    removeUserFromAccount: {
      entityAction: "Révoquer l'accès",
      title: "Révoquer l'accès au compte",
      body: "Cela retire l'appartenance manager de cet utilisateur. Il pourra être réinvité plus tard.",
      success: "Accès au compte révoqué",
      error: "Impossible de révoquer l'accès : {{message}}",
      selfNotAllowed: "Vous ne pouvez pas révoquer votre propre accès",
    },
    sendEventInvitationEmail: {
      entityAction: "Envoyer l'email",
      sending: "Envoi de l'email d'invitation à {{email}}…",
      success: "Email d'invitation envoyé",
      cooldown:
        "Un email a déjà été envoyé. Attendez une heure avant d'en envoyer un autre.",
      disabled: "L'envoi d'emails est désactivé.",
      error: "Impossible d'envoyer l'email : {{message}}",
    },
    cloneSpace: {
      entityAction: "Cloner le learning path",
      title: "Cloner le learning path",
      body: "Crée une copie complète de ce learning path et de son contenu. Cela peut prendre une minute.",
      success: "Learning path cloné",
      error: "Impossible de cloner le learning path : {{message}}",
    },
    learningPathImport: {
      button: "Importer JSON",
      entityAction: "Importer JSON",
      ariaLabel: "Importer un learning path depuis un fichier JSON",
      titleCreate: "Importer un learning path depuis JSON",
      titleUpdate: "Importer dans ce learning path",
      description:
        "Téléversez un learning-path.json avec les champs du path plus units, stories, questions et levels. Voir docs/content-import.",
      parseFailed: "Fichier invalide : {{message}}",
      graphFailed:
        "Le fichier a {{count}} erreur(s) de validation. Rien n'a été importé.",
      summary: "{{imported}} éléments importés, {{failed}} en échec",
      rowError: "{{key}} : {{reason}}",
      snackbarCreated: "Learning path créé ({{imported}} éléments, {{failed}} en échec)",
      snackbarUpdated: "Learning path mis à jour ({{imported}} éléments, {{failed}} en échec)",
      importFailed: "Échec de l'import : {{message}}",
    },
    contentImport: {
      button: "Importer JSON",
      ariaLabel: "Importer du contenu depuis un fichier JSON",
      title: "Importer {{collection}} depuis JSON",
      description:
        "Téléversez un fichier .json. Voir docs/content-import dans le dépôt pour le format et les exemples.",
      parseFailed: "Fichier invalide : {{message}}",
      summary: "{{imported}} importés, {{failed}} en échec",
      rowError: "{{key}} : {{reason}}",
      snackbarSummary: "{{imported}} importés, {{failed}} en échec",
      importFailed: "Échec de l'import : {{message}}",
    },
    notificationDefsImport: {
      button: "Importer JSON",
      ariaLabel: "Importer les modèles de notification depuis JSON",
      title: "Importer les modèles de notification",
      description:
        "Téléversez un fichier .json. Remplace ou crée chaque modèle par type. Voir docs/content-import (fixture + schéma).",
      parseFailed: "Fichier invalide : {{message}}",
      summary: "{{imported}} importés, {{failed}} en échec",
      rowError: "{{key}} : {{reason}}",
      snackbarSummary: "{{imported}} importés, {{failed}} en échec",
      importFailed: "Échec de l'import : {{message}}",
    },
    eventUserProfile: {
      column: "Profil",
      viewProfile: "Voir le profil",
      dialogTitle: "Profil du membre",
      dialogAria: "Profil de connexion du membre",
      close: "Fermer",
      noName: "Sans nom",
      emailVerified: "Email vérifié",
      emailUnverified: "Email non vérifié",
      accountActive: "Active",
      accountDisabled: "Désactivée",
      sectionContact: "Contact",
      sectionSignIn: "Comment il se connecte",
      sectionActivity: "Activité",
      email: "Email",
      phone: "Téléphone",
      noPhone: "Aucun téléphone",
      noProviders: "Aucune méthode de connexion",
      providerGoogle: "Google",
      providerPassword: "Email et mot de passe",
      providerApple: "Apple",
      providerPhone: "Téléphone",
      providerOther: "{{provider}}",
      createdAt: "Compte créé",
      lastSignIn: "Dernière connexion",
      lastActive: "Dernière activité",
      never: "Jamais",
      userId: "ID utilisateur",
      copyId: "Copier l'ID",
      copied: "Copié",
    },
    buildVersion: {
      current: "{{sha}}",
      updateAvailable: "Mettre à jour · {{sha}}",
      tooltipCurrent: "Build {{sha}} · {{builtAt}}",
      tooltipUpdate: "Un déploiement plus récent est disponible. Cliquez pour recharger. Build {{builtAt}}",
    },
    ...eventManagerTranslationsFr,
  },
};
